import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Dropdown, Form, Input, message, Modal, Popover, Space, Tag, Tooltip, Typography } from "antd";
import { EditOutlined, InfoCircleOutlined, MoreOutlined, PlusOutlined, ReloadOutlined, RobotOutlined, SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { formatDate } from "../../../../utils/dateUtils";
import { UTCDateRangePicker } from "../../../../components/DateTimeInput";
import AgentExecutionIndicator from "./editor/AgentExecutionIndicator";

dayjs.extend(utc);

const { Text, Title } = Typography;

const DRAFT_STATUS_META = {
    EDITING: { label: "编辑中", color: "blue" },
    DRAFT: { label: "编辑中", color: "blue" },
    FINALIZED: { label: "已定稿", color: "green" },
    PUBLISHED: { label: "已发布", color: "green" },
    ARCHIVED: { label: "已归档", color: "default" },
    CANCELLED: { label: "已取消", color: "default" },
};

const DRAFT_SOURCE_TYPE_LABELS = {
    BLANK: "空白创建",
    EXCEL_FILE: "Excel 文件导入",
    PASTED_TABLE: "粘贴表格导入",
    V1_SCHEDULE: "V1 计划导入",
    EXECUTION: "执行航班来源",
    LIVE_EXECUTION: "实时执行来源",
    MANUAL: "手工录入",
};

function formatDateRange(draft) {
    if (!draft?.period_start && !draft?.period_end) {
        return "未设定时间范围";
    }

    return `${formatDate(draft?.period_start) || "--"} 至 ${formatDate(draft?.period_end) || "--"}`;
}

function buildPeriodRange(draft) {
    const start = draft?.period_start ? dayjs.utc(draft.period_start) : null;
    const end = draft?.period_end ? dayjs.utc(draft.period_end) : null;
    if (!start?.isValid() || !end?.isValid()) {
        return undefined;
    }
    return [start, end];
}

function getDraftStatusMeta(status) {
    const normalizedStatus = String(status || "").toUpperCase();
    if (!normalizedStatus) {
        return null;
    }
    return DRAFT_STATUS_META[normalizedStatus] || { label: "状态待确认", color: "default" };
}

function getDraftSourceTypeLabel(sourceType) {
    const normalizedType = String(sourceType || "").toUpperCase();
    if (!normalizedType) {
        return null;
    }
    return DRAFT_SOURCE_TYPE_LABELS[normalizedType] || "来源待确认";
}

const ScheduleDraftToolbar = ({
    draft,
    canvasModel,
    onRefresh,
    refreshing,
    onOpenAgentTrigger,
    canTriggerAgent,
    triggerAgentDisabledReason,
    isAgentWorking,
    agentSessions = [],
    isFetchingAgentSessions,
    onOpenSourceTrack,
    canOpenSourceTrack,
    showFinalizeTrack,
    onFinalizeTrack,
    finalizeTrackDisabledReason,
    finalizeTrackTooltip,
    finalizingTrack,
    editToolbar,
    onUpdatePeriod,
    onUpdateDraft,
    updatingDraft,
    canUpdatePeriod = false,
    updatePeriodDisabledReason,
}) => {
    const [periodModalOpen, setPeriodModalOpen] = useState(false);
    const [nameEditing, setNameEditing] = useState(false);
    const [nameValue, setNameValue] = useState(draft?.name || "");
    const [nameSubmitting, setNameSubmitting] = useState(false);
    const nameInputRef = useRef(null);
    const skipNextNameBlurRef = useRef(false);
    const submittingNameRef = useRef(false);
    const [periodForm] = Form.useForm();
    const periodRange = useMemo(() => buildPeriodRange(draft), [draft]);
    const periodText = formatDateRange(draft);
    const draftName = nameValue || draft?.name || "航班计划";
    const statusMeta = getDraftStatusMeta(draft?.status);
    const sourceTypeLabel = getDraftSourceTypeLabel(draft?.source_type);
    const periodDisabled = !canUpdatePeriod || updatingDraft;
    const periodTooltip = canUpdatePeriod ? "修改工作区查看、导入和计算时间段" : updatePeriodDisabledReason || "当前不可修改时间段";
    const nameDisabled = !onUpdateDraft || !canUpdatePeriod || updatingDraft || nameSubmitting;
    const nameTooltip = nameDisabled
        ? (!onUpdateDraft ? "草稿更新入口不可用" : updatingDraft || nameSubmitting ? "正在更新草稿" : updatePeriodDisabledReason || "当前不可修改 Draft 名称")
        : "点击修改 Draft 名称";
    const secondaryStats = [
        `${draft?.active_flight_count || 0} 个航班`,
        `${canvasModel?.editableTrackCount || 0} 条编辑轨`,
        `${canvasModel?.referenceTrackCount || 0} 条参考轨`,
    ];
    const secondaryActionItems = [
        {
            key: "source",
            icon: <PlusOutlined />,
            label: "添加来源 / 导入",
            disabled: !canOpenSourceTrack,
        },
        {
            key: "agent",
            icon: <RobotOutlined />,
            label: canTriggerAgent ? "Agent 编排" : triggerAgentDisabledReason || "Agent 编排不可用",
            disabled: !canTriggerAgent,
        },
        {
            key: "refresh",
            icon: <ReloadOutlined />,
            label: refreshing ? "刷新中" : "刷新计划",
            disabled: refreshing,
        },
    ];

    const handleSecondaryAction = useCallback(
        ({ key }) => {
            if (key === "source") {
                onOpenSourceTrack?.();
                return;
            }
            if (key === "agent") {
                onOpenAgentTrigger?.();
                return;
            }
            if (key === "refresh") {
                onRefresh?.();
            }
        },
        [onOpenAgentTrigger, onOpenSourceTrack, onRefresh],
    );

    const secondaryInfoContent = (
        <Space direction="vertical" size={6} className="schedule-draft-toolbar-info-popover">
            <Text strong>辅助信息</Text>
            <Text type="secondary">来源：{sourceTypeLabel || "未记录"}</Text>
            {secondaryStats.map((item) => <Text key={item} type="secondary">{item}</Text>)}
        </Space>
    );

    useEffect(() => {
        if (periodModalOpen) {
            periodForm.setFieldsValue({ periodRange });
        }
    }, [periodForm, periodModalOpen, periodRange]);

    useEffect(() => {
        if (!nameEditing) {
            setNameValue(draft?.name || "");
        }
    }, [draft?.name, nameEditing]);

    useEffect(() => {
        if (!nameEditing) {
            return undefined;
        }

        const frame = window.requestAnimationFrame(() => {
            nameInputRef.current?.focus?.({ cursor: "all" });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [nameEditing]);

    const focusNameInput = useCallback(() => {
        window.requestAnimationFrame(() => {
            nameInputRef.current?.focus?.({ cursor: "all" });
        });
    }, []);

    const skipPendingNameBlur = useCallback(() => {
        skipNextNameBlurRef.current = true;
        window.setTimeout(() => {
            skipNextNameBlurRef.current = false;
        }, 0);
    }, []);

    const startNameEdit = useCallback(() => {
        if (nameDisabled) {
            return;
        }
        setNameValue(draft?.name || "");
        setNameEditing(true);
    }, [draft?.name, nameDisabled]);

    const cancelNameEdit = useCallback(() => {
        skipPendingNameBlur();
        setNameValue(draft?.name || "");
        setNameEditing(false);
    }, [draft?.name, skipPendingNameBlur]);

    const submitNameEdit = useCallback(async () => {
        if (submittingNameRef.current) {
            return false;
        }

        const nextName = String(nameValue || "").trim();
        const currentName = String(draft?.name || "").trim();
        if (!nextName) {
            message.warning("Draft 名称不能为空");
            focusNameInput();
            return false;
        }
        if (nextName.length > 100) {
            message.warning("Draft 名称不能超过 100 个字符");
            focusNameInput();
            return false;
        }
        if (nextName === currentName) {
            skipPendingNameBlur();
            setNameValue(draft?.name || "");
            setNameEditing(false);
            return true;
        }

        submittingNameRef.current = true;
        setNameSubmitting(true);
        try {
            const result = await onUpdateDraft?.({ name: nextName });
            if (!result) {
                focusNameInput();
                return false;
            }
            message.success("Draft 名称已更新");
            skipPendingNameBlur();
            setNameValue(nextName);
            setNameEditing(false);
            return true;
        } finally {
            submittingNameRef.current = false;
            setNameSubmitting(false);
        }
    }, [draft?.name, focusNameInput, nameValue, onUpdateDraft, skipPendingNameBlur]);

    const handleNameInputKeyDown = useCallback(
        (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                cancelNameEdit();
                return;
            }
            if (event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                void submitNameEdit();
            }
        },
        [cancelNameEdit, submitNameEdit],
    );

    const handleNameInputBlur = useCallback(() => {
        if (skipNextNameBlurRef.current) {
            skipNextNameBlurRef.current = false;
            return;
        }
        void submitNameEdit();
    }, [submitNameEdit]);

    const openPeriodModal = useCallback(() => {
        periodForm.setFieldsValue({ periodRange });
        setPeriodModalOpen(true);
    }, [periodForm, periodRange]);

    const closePeriodModal = useCallback(() => {
        setPeriodModalOpen(false);
    }, []);

    const handleSubmitPeriod = useCallback(async () => {
        const values = await periodForm.validateFields();
        const nextRange = values.periodRange || [];
        const payload = {
            period_start: nextRange[0]?.format("YYYY-MM-DD"),
            period_end: nextRange[1]?.format("YYYY-MM-DD"),
        };
        const result = await onUpdatePeriod?.(payload);
        if (!result) {
            return;
        }
        message.success("时间段已更新，实时参考窗口会按新范围刷新；匹配审查可能需要重新预览/确认");
        closePeriodModal();
    }, [closePeriodModal, onUpdatePeriod, periodForm]);

    return (
        <Card className="schedule-draft-toolbar-card" variant="borderless">
            <div className="schedule-draft-toolbar">
                <div className="schedule-draft-toolbar-main">
                    <div className="schedule-draft-toolbar-title-row">
                        {nameEditing ? (
                            <Input
                                ref={nameInputRef}
                                size="small"
                                className="schedule-draft-toolbar-name-input"
                                aria-label="Draft 名称"
                                value={nameValue}
                                maxLength={100}
                                disabled={nameSubmitting || updatingDraft}
                                onChange={(event) => setNameValue(event.target.value)}
                                onKeyDown={handleNameInputKeyDown}
                                onBlur={handleNameInputBlur}
                            />
                        ) : (
                            <Title level={4} className="schedule-draft-toolbar-title">
                                <Tooltip title={nameTooltip}>
                                    <button type="button" className="schedule-draft-toolbar-title-button" disabled={nameDisabled} onClick={startNameEdit}>
                                        {draftName}
                                    </button>
                                </Tooltip>
                            </Title>
                        )}
                        {!nameEditing ? (
                            <Tooltip title={nameTooltip}>
                                <span>
                                    <Button
                                        type="text"
                                        size="small"
                                        className="schedule-draft-toolbar-name-edit-button"
                                        icon={<EditOutlined />}
                                        aria-label="编辑 Draft 名称"
                                        onClick={startNameEdit}
                                        disabled={nameDisabled}
                                        loading={nameSubmitting}
                                    />
                                </span>
                            </Tooltip>
                        ) : null}
                    </div>
                    <Space wrap size={[6, 6]} className="schedule-draft-toolbar-meta">
                        {statusMeta ? <Tag color={statusMeta.color}>{statusMeta.label}</Tag> : null}
                        <Tooltip title={periodTooltip}>
                            <span>
                                <Button type="link" size="small" className="schedule-draft-toolbar-period-trigger" onClick={openPeriodModal} disabled={periodDisabled}>
                                    {periodText}
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title={periodTooltip}>
                            <span>
                                <Button
                                    type="text"
                                    size="small"
                                    className="schedule-draft-toolbar-period-edit-button"
                                    icon={<EditOutlined />}
                                    aria-label="编辑时间段"
                                    onClick={openPeriodModal}
                                    disabled={periodDisabled}
                                    loading={updatingDraft}
                                />
                            </span>
                        </Tooltip>
                    </Space>
                    {isAgentWorking ? <AgentExecutionIndicator sessions={agentSessions} isFetching={isFetchingAgentSessions} /> : null}
                </div>

                <Space wrap size={[8, 8]} className="schedule-draft-toolbar-actions">
                    {editToolbar ? <div className="schedule-draft-toolbar-edit-actions">{editToolbar}</div> : null}
                    {showFinalizeTrack ? (
                        <Tooltip title={finalizeTrackDisabledReason || finalizeTrackTooltip || "发布当前编辑轨到执行层"}>
                            <Button
                                type="primary"
                                danger
                                icon={<SendOutlined />}
                                onClick={onFinalizeTrack}
                                disabled={Boolean(finalizeTrackDisabledReason) || finalizingTrack}
                                loading={finalizingTrack}
                            >
                                定稿发布
                            </Button>
                        </Tooltip>
                    ) : null}
                    <Popover trigger="click" placement="bottomRight" content={secondaryInfoContent}>
                        <Button icon={<InfoCircleOutlined />}>信息</Button>
                    </Popover>
                    <Dropdown menu={{ items: secondaryActionItems, onClick: handleSecondaryAction }} trigger={["click"]} placement="bottomRight">
                        <Button icon={<MoreOutlined />}>更多</Button>
                    </Dropdown>
                </Space>
            </div>
            <Modal
                title="修改时间段"
                open={periodModalOpen}
                onOk={handleSubmitPeriod}
                onCancel={closePeriodModal}
                okText="保存时间段"
                cancelText="取消"
                confirmLoading={updatingDraft}
                destroyOnHidden
            >
                <Form form={periodForm} layout="vertical">
                    <Form.Item name="periodRange" label="编排时间范围" rules={[{ required: true, message: "请选择时间范围" }]}>
                        <UTCDateRangePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder={["开始日期", "结束日期"]} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default ScheduleDraftToolbar;
